import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginInput, loginInputSchema } from 'chery-contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isUnauthorizedError } from '../../apis/orpc-client';
import { useAuth } from '../../auth/AuthContext';
import { nextPathAfterSignIn } from '../../auth/landing-path';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { AuthCard, FormAlert } from './AuthCard';

interface LocationState {
  from?: string;
}

export const LoginPage = () => {
  const { t } = useI18nTranslation();
  const { user, isAuthenticated, isBootstrapping, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated && user && !isBootstrapping) {
    return (
      <Navigate
        to={nextPathAfterSignIn(user, from)}
        replace
      />
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      const signedIn = await login(values);

      navigate(nextPathAfterSignIn(signedIn, from), { replace: true });
    } catch (error) {
      setSubmitError(isUnauthorizedError(error) ? t('login.invalidCredentials') : t('login.failed'));
    }
  });

  return (
    <AuthCard
      title={t('login.title')}
      subtitle={t('login.subtitle')}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-6 space-y-4"
      >
        <FormField
          id="email"
          label={t('login.email')}
          error={errors.email?.message}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder={t('login.emailPlaceholder')}
            hasError={Boolean(errors.email)}
            {...register('email')}
          />
        </FormField>

        <FormField
          id="password"
          label={t('login.password')}
          error={errors.password?.message}
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder={t('login.passwordPlaceholder')}
            hasError={Boolean(errors.password)}
            {...register('password')}
          />
        </FormField>

        <FormAlert message={submitError} />

        <Button
          type="submit"
          isLoading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
    </AuthCard>
  );
};
